/* eslint-disable */
"use client";

import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "./theme";
import { formatCompactPeso, formatPeso } from "./utils";
import { RollingNumber } from "./rolling-number";

const PALETTES = {
  dark: {
    gross: "#3f7dff",
    net: "#ffc927",
    tick: "#5f7196",
    track: "#1a2a48",
    tooltipBg: "#0d1729",
    tooltipBorder: "#1e3050",
    text: "#eaf0ff",
  },
  light: {
    gross: "#1f63e8",
    net: "#f5b700",
    tick: "#8a99bd",
    track: "#dbe6fa",
    tooltipBg: "#ffffff",
    tooltipBorder: "#d8e3f6",
    text: "#0b1830",
  },
};

export type TrendPoint = { label: string; gross: number; net: number };
export type SlicePoint = { name: string; value: number };

function ChartTooltip({
  active,
  payload,
  label,
  colors,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color?: string }>;
  label?: string;
  colors: (typeof PALETTES)["dark"];
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-xl border px-3.5 py-2.5 text-xs shadow-xl"
      style={{ background: colors.tooltipBg, borderColor: colors.tooltipBorder, color: colors.text }}
    >
      {label && <p className="mb-1 font-bold">{label}</p>}
      {payload.map((p) => (
        <p key={p.name} className="flex items-center gap-2 font-medium">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-bold">{formatPeso(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

export function PayrollTrendChart({ data }: { data: TrendPoint[] }) {
  const { theme } = useTheme();
  const c = PALETTES[theme] ?? PALETTES.dark;
  if (!data.length) {
    return <div className="flex h-[260px] items-center justify-center text-sm text-faint">No payroll runs yet.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 4, left: -8, bottom: 0 }} barGap={4}>
        <XAxis
          dataKey="label"
          tick={{ fill: c.tick, fontSize: 11, fontWeight: 600 }}
          axisLine={{ stroke: c.track }}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fill: c.tick, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v: number) => formatCompactPeso(v)}
          width={64}
        />
        <Tooltip content={<ChartTooltip colors={c} />} cursor={{ fill: c.track, opacity: 0.35 }} />
        <Bar dataKey="gross" name="Gross pay" fill={c.gross} radius={[6, 6, 0, 0]} maxBarSize={26} />
        <Bar dataKey="net" name="Net pay" fill={c.net} radius={[6, 6, 0, 0]} maxBarSize={26} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function EmployeeTypeDonut({ data, total }: { data: SlicePoint[]; total: number }) {
  const { theme } = useTheme();
  const c = PALETTES[theme] ?? PALETTES.dark;
  const colors = [c.gross, c.net];
  if (!total) {
    return <div className="flex h-[220px] items-center justify-center text-sm text-faint">No employees enrolled yet.</div>;
  }
  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={68}
            outerRadius={92}
            paddingAngle={4}
            strokeWidth={0}
            cornerRadius={8}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={colors[i % colors.length]} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip colors={c} />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <RollingNumber text={String(total)} className="font-display text-3xl font-bold text-text" />
        <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">Active</p>
      </div>
    </div>
  );
}

export function DeptCostBars({
  data,
}: {
  data: Array<{ department: string; gross: number }>;
}) {
  const max = Math.max(1, ...data.map((d) => d.gross));
  if (!data.length) {
    return <div className="flex h-[200px] items-center justify-center text-sm text-faint">Run payroll to see cost per department.</div>;
  }
  return (
    <ul className="space-y-4">
      {data.map((d, i) => (
        <li key={d.department}>
          <div className="mb-1.5 flex items-baseline justify-between text-xs">
            <span className="font-semibold text-text">{d.department}</span>
            <RollingNumber text={formatPeso(d.gross, { decimals: 0 })} className="font-bold text-muted" />
          </div>
          <div className="h-2.5 overflow-hidden rounded-full bg-[var(--chart-track)]">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.max(4, (d.gross / max) * 100)}%`,
                background: i % 2 === 0 ? "var(--primary)" : "var(--accent)",
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
